import { Box, Card, IconButton, Stack } from '@mui/material';
import { TrashIcon } from '@phosphor-icons/react';
import {
  FLOW_FILTER_TYPE_SUPPORTED_OPS,
  FlowCaseSensitivity,
  FlowCaseSensitivityDataTypes,
  FlowFieldDataType,
  type FlowFilterCondition,
  FlowFilterOperation,
} from 'flow-schema';

import { colors, isDefined } from 'common';

import { FILTER_BLOCK_NULL_OPERATIONS, FILTER_BLOCK_OPERATION_NAMES } from './constants';
import { useReactFlowViewContext } from '../../../context/context';
import { BlockValueTemplateInnerField } from '../../ConfigFields/CodeMirror/FlowTemplate';
import { BlockInnerSelectField } from '../../ConfigFields/SelectField';
import { useSelectedNodeField } from '../../ConfigFields/useSelectedNodeField';

const DATA_TYPE_OPTIONS = Object.values(FlowFieldDataType).map((entry) => ({
  label: entry,
  value: entry,
}));

const CASE_SENSITIVITY_OPTIONS = Object.values(FlowCaseSensitivity).map((entry) => ({
  label: entry,
  value: entry,
}));

const OPERATION_OPTIONS_BY_DATATYPE = Object.fromEntries(
  Object.entries(FLOW_FILTER_TYPE_SUPPORTED_OPS).map(
    ([dataType, supportedOps]: [string, Set<FlowFilterOperation>]) => {
      const options = Object.values(FlowFilterOperation)
        .filter((entry) => supportedOps.has(entry))
        .map((entry) => ({
          label: FILTER_BLOCK_OPERATION_NAMES[entry],
          value: entry,
        }));
      return [dataType, options];
    }
  )
);

interface FilterListBlockFilterConditionCardProps {
  fieldNamePrefix: string;
  onDelete?: () => void;
  filterCondition: FlowFilterCondition;
}

export function FilterListBlockFilterConditionCard(props: FilterListBlockFilterConditionCardProps) {
  const { onDelete, fieldNamePrefix, filterCondition } = props;
  const { isEditable } = useReactFlowViewContext();
  const { setValue: setOperation } = useSelectedNodeField<FlowFilterOperation>(
    `${fieldNamePrefix}.operation`
  );

  return (
    <Card
      variant="outlined"
      sx={{
        paddingX: 1,
        paddingY: 1.5,
        backgroundColor: colors.extended.gray[97],
        overflow: 'visible',
      }}
    >
      <Stack direction="row" gap={1} alignItems="flex-start">
        <Stack gap={1} flex={1} minWidth={0}>
          <BlockValueTemplateInnerField
            fieldId={`fieldToFilter-${filterCondition.id}`}
            fieldName={`${fieldNamePrefix}.fieldToFilter`}
            label="Field to filter"
            placeholder="Name of the field within each object to filter on"
            required
            isMultiline
          />
          <Stack direction="row" gap={1}>
            <BlockInnerSelectField
              fieldName={`${fieldNamePrefix}.dataType`}
              options={DATA_TYPE_OPTIONS}
              aria-label="Data type"
              sx={{
                backgroundColor: 'background.paper',
                flex: 1,
              }}
              onChange={(event) => {
                const dataType = event.target.value as FlowFieldDataType;
                dataType === FlowFieldDataType.List
                  ? setOperation(FlowFilterOperation.Contains)
                  : setOperation(FlowFilterOperation.Equals);
              }}
              clearable={false}
            />
            <BlockInnerSelectField
              fieldName={`${fieldNamePrefix}.operation`}
              options={OPERATION_OPTIONS_BY_DATATYPE[filterCondition.dataType]}
              aria-label="Operation"
              variant="borderless"
              sx={{
                flex: 1,
                textAlign: 'center',
                '& :focus': { backgroundColor: 'transparent' },
              }}
              clearable={false}
            />
            {FlowCaseSensitivityDataTypes.includes(filterCondition.dataType) &&
              !FILTER_BLOCK_NULL_OPERATIONS.has(filterCondition.operation) && (
                <BlockInnerSelectField
                  fieldName={`${fieldNamePrefix}.caseSensitivity`}
                  options={CASE_SENSITIVITY_OPTIONS}
                  aria-label="Case sensitivity"
                  sx={{
                    backgroundColor: 'background.paper',
                    flex: 1,
                  }}
                  clearable={false}
                />
              )}
          </Stack>
          {!FILTER_BLOCK_NULL_OPERATIONS.has(filterCondition.operation) && (
            <BlockValueTemplateInnerField
              fieldId={`value-${filterCondition.id}`}
              fieldName={`${fieldNamePrefix}.value`}
              label="Filter Value"
              placeholder="Enter value to match against"
              required
              isMultiline
            />
          )}
        </Stack>
        <Box marginTop="4px">
          <IconButton
            aria-label="Delete Field"
            size="medium"
            onClick={onDelete && (() => onDelete())}
            disabled={!isEditable || !isDefined(onDelete)}
          >
            <TrashIcon size={16} />
          </IconButton>
        </Box>
      </Stack>
    </Card>
  );
}
